// <TimeViewToolbar> — T-143 + T-147 (pixel polish).
//
// The shell-level toolbar that sits above both tabs (Calendar + Manage
// Requests). All controls are URL-driven via `useTimeViewUrlState` so
// deep-links work and back/forward navigation restores state.
//
// Spec references:
//   - Tab switch pills (Calendar / Manage requests) → spec §1.1, §4
//   - Week navigation + Today button (Calendar tab only) → spec §5.1
//   - Site / Team / Request type filters → spec §5.4 (popover panel)
//   - Date-range preset dropdown → spec §7.1
//
// T-147 polish on top of T-143:
//   - Active tab pill swapped to wine-red `#A6264C` per spec §1 identity.
//   - Today button border swapped to wine-red.
//   - Filter dropdowns upgraded from inline `<select>` to button+popover
//     per spec §5.4 (still URL-driven via the same setters).
//   - Date-range preset dropdown ("This week" / "This month" / "Custom")
//     per spec §7.1 — visible-only for now; routes through `setWeekStart`
//     when "This week" is picked, otherwise leaves the week state intact.

import { useContext, useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import type { CalendarViewMode, TimeOffType } from '@pa/shared/time-view';
import {
  isoMonday,
  shiftIsoDate,
  useTimeViewUrlState,
  type TimeViewTab,
} from './timeViewUrlState';
import { useViewMode } from './useViewMode';

// Time-view identity accent (spec §1).
const ACCENT = '#A6264C';

const TIME_OFF_TYPES: TimeOffType[] = [
  'Vacation',
  'Sick',
  'Child Sick',
  'Work from Home',
  'Parental Leave',
];

interface FilterOption {
  id: string;
  name: string;
}

interface Props {
  /** Optional list of sites to render in the Site filter. Default empty. */
  sites?: FilterOption[];
  /** Optional list of teams to render in the Team filter. Default empty. */
  teams?: FilterOption[];
  /**
   * User id for per-user view-mode persistence (T-229's `useViewMode`).
   * Defaulted to `'anonymous'` so tests that don't wire a user still
   * exercise the segment without needing AuthProvider.
   */
  userId?: string;
}

export default function TimeViewToolbar({
  sites = [],
  teams = [],
  userId = 'anonymous',
}: Props) {
  const url = useTimeViewUrlState();
  const { state } = url;
  const showCalendarControls = state.tab === 'calendar';
  const today = isoMonday();
  const isOnCurrentWeek = state.weekStart === today;
  // T-230 — view-mode segment (Month / Week). Per-user-keyed via the
  // active session's user id (passed from <TimeView> which reads
  // AuthContext); falls back to `'anonymous'` for tests + when the
  // session hasn't resolved yet.
  const { viewMode, setViewMode } = useViewMode(userId);

  return (
    <div
      className="px-8 py-3 border-b border-slate-100 bg-slate-50/50 flex flex-wrap items-center justify-between gap-3"
      data-testid="time-view-toolbar"
    >
      {/* Left cluster — tab switch + (Calendar-only) week nav */}
      <div className="flex items-center gap-4">
        <div
          className="inline-flex rounded-full bg-slate-100 p-1"
          role="tablist"
          data-testid="time-view-tabs"
        >
          <TabPill
            label="Calendar"
            tab="calendar"
            active={state.tab === 'calendar'}
            onClick={() => url.setTab('calendar')}
          />
          <TabPill
            label="Manage Requests"
            tab="manage"
            active={state.tab === 'manage'}
            onClick={() => url.setTab('manage')}
          />
        </div>

        {showCalendarControls && (
          <div
            className="flex items-center gap-2"
            data-testid="time-view-week-nav"
          >
            <PresetDropdown
              weekStart={state.weekStart}
              onPickThisWeek={() => url.resetToToday()}
            />
            <button
              type="button"
              onClick={() => url.shiftWeek(-1)}
              className="p-1.5 hover:bg-slate-200 rounded-full text-slate-600"
              aria-label="Previous week"
              data-testid="time-view-week-prev"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              type="button"
              onClick={() => url.resetToToday()}
              disabled={isOnCurrentWeek}
              className={
                'px-3 py-1 text-xs font-bold rounded-lg border disabled:opacity-40 disabled:cursor-not-allowed'
              }
              style={{
                color: ACCENT,
                borderColor: `${ACCENT}55`,
              }}
              data-testid="time-view-today-btn"
            >
              Today
            </button>
            <button
              type="button"
              onClick={() => url.shiftWeek(1)}
              className="p-1.5 hover:bg-slate-200 rounded-full text-slate-600"
              aria-label="Next week"
              data-testid="time-view-week-next"
            >
              <ChevronRight size={16} />
            </button>
            <span
              className="text-sm font-bold text-slate-700 ml-1"
              data-testid="time-view-week-label"
            >
              {formatWeekLabel(state.weekStart)}
            </span>
          </div>
        )}

        {showCalendarControls && (
          <ViewModeSegment
            mode={viewMode}
            onChange={setViewMode}
          />
        )}

        {/* T-231 — Hide-weekends toggle is Week-mode + Calendar-tab only. */}
        {showCalendarControls && viewMode === 'Week' && (
          <button
            type="button"
            data-testid="time-view-hide-weekends"
            aria-pressed={state.hideWeekends}
            onClick={() => url.setHideWeekends(!state.hideWeekends)}
            className={
              'px-3 py-1 text-xs font-medium rounded-full border transition-colors ' +
              (state.hideWeekends
                ? 'bg-slate-800 text-white border-slate-800'
                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50')
            }
          >
            {state.hideWeekends ? 'Show weekends' : 'Hide weekends'}
          </button>
        )}
      </div>

      {/* Right cluster — filter popovers */}
      <div className="flex items-center gap-3">
        <FilterPopover
          testId="time-view-filter-site"
          label="Site"
          value={state.site}
          onChange={url.setSite}
          options={[
            { value: 'all', label: 'All sites' },
            ...sites.map((s) => ({ value: s.id, label: s.name })),
          ]}
        />
        <FilterPopover
          testId="time-view-filter-team"
          label="Team"
          value={state.team}
          onChange={url.setTeam}
          options={[
            { value: 'all', label: 'All teams' },
            ...teams.map((t) => ({ value: t.id, label: t.name })),
          ]}
        />
        {showCalendarControls && (
          <FilterPopover
            testId="time-view-filter-type"
            label="Request type"
            value={state.type}
            onChange={(v) => url.setType(v as TimeOffType | 'all')}
            options={[
              { value: 'all', label: 'All types' },
              ...TIME_OFF_TYPES.map((t) => ({ value: t, label: t })),
            ]}
          />
        )}
      </div>
    </div>
  );
}

// ── pieces ─────────────────────────────────────────────────────────

/**
 * View-mode segment — Month / Week. T-230 (time-view-week-implementation-
 * plan.md §6.2). Mirrors the wine-red accent treatment used on the
 * primary tab pills. The hook `useViewMode` (T-229) owns URL +
 * localStorage persistence; this component is the thin visual surface.
 */
function ViewModeSegment({
  mode,
  onChange,
}: {
  mode: CalendarViewMode;
  onChange: (next: CalendarViewMode) => void;
}) {
  return (
    <div
      className="inline-flex rounded-full bg-slate-100 p-1"
      data-testid="time-view-view-mode"
      role="tablist"
      aria-label="Calendar view mode"
    >
      {(['Month', 'Week'] as const).map((m) => {
        const active = mode === m;
        return (
          <button
            key={m}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(m)}
            data-testid={`time-view-view-mode-${m.toLowerCase()}`}
            style={active ? { backgroundColor: ACCENT, color: '#ffffff' } : undefined}
            className={
              'px-3 py-1 text-xs font-medium rounded-full transition-colors ' +
              (active ? 'shadow-sm' : 'text-slate-600 hover:text-slate-800')
            }
          >
            {m}
          </button>
        );
      })}
    </div>
  );
}

function TabPill({
  label,
  tab,
  active,
  onClick,
}: {
  label: string;
  tab: TimeViewTab;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      data-testid={`time-view-tab-${tab}`}
      style={
        active
          ? { backgroundColor: ACCENT, color: '#ffffff' }
          : undefined
      }
      className={
        'px-4 py-1.5 text-sm font-medium rounded-full transition-colors ' +
        (active ? 'shadow-sm' : 'text-slate-600 hover:text-slate-800')
      }
    >
      {label}
    </button>
  );
}

/**
 * Button + popover replacement for the inline `<select>` filter
 * controls. Same URL contract — the popover closes on outside click
 * and on selection. Spec §5.4.
 */
function FilterPopover({
  label,
  value,
  onChange,
  options,
  testId,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  options: Array<{ value: string; label: string }>;
  testId: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  // Outside-click / Esc closes the popover.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const current = options.find((o) => o.value === value) ?? options[0];
  const isActive = value !== 'all';

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={label}
        aria-expanded={open}
        data-testid={testId}
        data-value={value}
        className={
          'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium ' +
          (isActive
            ? 'border-slate-300 bg-white text-slate-700'
            : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50')
        }
      >
        <span className="text-slate-400 mr-0.5">{label}:</span>
        <span>{current?.label ?? value}</span>
        <ChevronDown size={12} className="text-slate-400" />
      </button>
      {open && (
        <div
          data-testid={`${testId}-popover`}
          className="absolute z-30 right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-md p-1 min-w-[180px] max-h-[280px] overflow-auto"
        >
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              data-testid={`${testId}-option-${opt.value}`}
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
              className={
                'w-full text-left px-3 py-1.5 text-xs rounded ' +
                (opt.value === value
                  ? 'bg-slate-100 text-slate-800 font-semibold'
                  : 'text-slate-700 hover:bg-slate-50')
              }
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Tiny date-range preset dropdown per spec §7.1 — "This week" /
 * "This month" / "Custom range...". The Calendar tab is a week-pivoted
 * view, so the visible options for now are:
 *
 *   - "This week" → snaps `weekStart` to today's Monday.
 *   - "Custom" → no-op; the prev/next week buttons let the user steer.
 *
 * The "This month" option is reserved for the future month roster; we
 * still render it so the dropdown shape matches the spec.
 */
function PresetDropdown({
  weekStart,
  onPickThisWeek,
}: {
  weekStart: string;
  onPickThisWeek: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const isThisWeek = weekStart === isoMonday();
  const label = isThisWeek ? 'This week' : 'Custom';

  return (
    <div className="relative" ref={ref}>
      <span className="text-xs text-slate-500 font-medium mr-2">Preset:</span>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="time-view-preset"
        className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-700 bg-white border border-slate-200 rounded-lg px-3 py-1.5 hover:bg-slate-50"
      >
        <span>{label}</span>
        <ChevronDown size={12} className="text-slate-400" />
      </button>
      {open && (
        <div
          data-testid="time-view-preset-popover"
          className="absolute z-30 left-12 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-md p-1 min-w-[160px]"
        >
          <button
            type="button"
            data-testid="time-view-preset-this-week"
            onClick={() => {
              onPickThisWeek();
              setOpen(false);
            }}
            className={
              'w-full text-left px-3 py-1.5 text-xs rounded ' +
              (isThisWeek
                ? 'bg-slate-100 text-slate-800 font-semibold'
                : 'text-slate-700 hover:bg-slate-50')
            }
          >
            This week
          </button>
          <button
            type="button"
            data-testid="time-view-preset-this-month"
            disabled
            title="Month view coming soon"
            className="w-full text-left px-3 py-1.5 text-xs rounded text-slate-400 cursor-not-allowed"
          >
            This month
          </button>
          <button
            type="button"
            data-testid="time-view-preset-custom"
            onClick={() => setOpen(false)}
            className={
              'w-full text-left px-3 py-1.5 text-xs rounded ' +
              (!isThisWeek
                ? 'bg-slate-100 text-slate-800 font-semibold'
                : 'text-slate-700 hover:bg-slate-50')
            }
          >
            Custom
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Format a Monday ISO date as "MMM D – D, YYYY" (or crossing months/years).
 * Visible-only — does not affect URL state.
 */
function formatWeekLabel(mondayIso: string): string {
  const sundayIso = shiftIsoDate(mondayIso, 6);
  const [my, mm, md] = mondayIso.split('-').map(Number);
  const [sy, sm, sd] = sundayIso.split('-').map(Number);
  const start = new Date(Date.UTC(my, mm - 1, md));
  const end = new Date(Date.UTC(sy, sm - 1, sd));
  const monthFmt = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    timeZone: 'UTC',
  });
  const startMonth = monthFmt.format(start);
  const endMonth = monthFmt.format(end);
  if (start.getUTCFullYear() !== end.getUTCFullYear()) {
    return `${startMonth} ${start.getUTCDate()}, ${start.getUTCFullYear()} – ${endMonth} ${end.getUTCDate()}, ${end.getUTCFullYear()}`;
  }
  if (start.getUTCMonth() !== end.getUTCMonth()) {
    return `${startMonth} ${start.getUTCDate()} – ${endMonth} ${end.getUTCDate()}, ${end.getUTCFullYear()}`;
  }
  return `${startMonth} ${start.getUTCDate()} – ${end.getUTCDate()}, ${end.getUTCFullYear()}`;
}
